/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AuthenticationCodesFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing Authentication Rules.
    Manages authentication rule configuration with tabs for Rule and Codes.

 🔧 Notes:
    - Domain-scoped data
    - Auto-generates context from name
    - 2 tabs: Rule and Codes

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Key, Plus, Settings, Trash2, Upload, Search, Copy, X } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { FormBox } from '../../components/ui/FormBox';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import RecordingSelect from '../../components/ui/RecordingSelect';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import Toggle from '../../components/ui/Toggle';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import ImportCodesModal from './ImportCodesModal';
import { navigateToAuthenticationRuleDuplicate } from './authenticationCodesActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import {
  fetchAuthenticationRule,
  createAuthenticationRule,
  updateAuthenticationRule,
  deleteAuthenticationRule,
  createAuthenticationCode,
  updateAuthenticationCode,
  deleteAuthenticationCode,
  fetchAuthenticationRules,
  importAuthenticationCodes,
} from './api';
import { AuthenticationCode, AuthenticationRule, AuthenticationRuleFormState } from './types';

type RuleSettings = Record<string, unknown>;

const DEFAULT_RULE: AuthenticationRuleFormState = {
  name: '',
  context: '',
  promptRecordingId: null,
  timeoutSeconds: 10,
  maxAttempts: 3,
  destinationMatchModule: '',
  destinationMatchData: '',
  destinationFailModule: '',
  destinationFailData: '',
  enabled: true,
  settings: {},
};

const deepClone = <T,>(value: T): T =>
  typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));

const generateContextFromName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 80)
    .replace(/^auth_/, '')
    .replace(/^/, 'auth_');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeRuleResponse = (payload: any): AuthenticationRuleFormState => ({
  name: payload.name || '',
  context: payload.context || '',
  promptRecordingId: payload.promptRecordingId ?? null,
  timeoutSeconds: payload.timeoutSeconds ?? 10,
  maxAttempts: payload.maxAttempts ?? 3,
  destinationMatchModule: payload.destinationMatchModule || '',
  destinationMatchData: payload.destinationMatchData || '',
  destinationFailModule: payload.destinationFailModule || '',
  destinationFailData: payload.destinationFailData || '',
  enabled: Boolean(payload.enabled ?? true),
  settings: payload.settings ?? {},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeCodeResponse = (codes: any[]): AuthenticationCode[] =>
  Array.isArray(codes)
    ? codes.map((code) => ({
      id: code.id,
      code: code.code || '',
      label: code.label || '',
      enabled: Boolean(code.enabled ?? true),
      usedCount: code.usedCount ?? code.used_count,
      lastUsedAt: code.lastUsedAt || code.last_used_at || null,
      lastCaller: code.lastCaller || code.last_caller || '',
      settings: code.settings || {},
    }))
    : [];

import { AuthCodesTab } from './components/AuthCodesTab';

export default function AuthenticationCodesFormView() {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<AuthenticationRuleFormState>(deepClone(DEFAULT_RULE));
  const [codes, setCodes] = useState<AuthenticationCode[]>([]);
  const [originalRule, setOriginalRule] = useState<AuthenticationRuleFormState>(deepClone(DEFAULT_RULE));
  const [originalCodes, setOriginalCodes] = useState<AuthenticationCode[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'rule' | 'codes'>('rule');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteCodeModal, setShowDeleteCodeModal] = useState<{ isOpen: boolean; codeIndex: number | null }>({
    isOpen: false,
    codeIndex: null,
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const lastSaveTimestamp = useRef(0);

  // Quick Search state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; description?: string | null }>>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Refs to prevent infinite loops
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Update refs when values change
  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(deepClone(DEFAULT_RULE));
      setCodes([]);
      setOriginalRule(deepClone(DEFAULT_RULE));
      setOriginalCodes([]);
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, resetParam, searchParams]);

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('authenticationCodes.editEntry')} - ${formData.name || ''}` : t('authenticationCodes.createEntry')),
    [isEditMode, t, formData.name],
  );

  const loadRule = useCallback(
    async (ruleId: string, options: { duplicate: boolean }) => {
      if (!ruleId || ruleId === 'new') {
        setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // Prevent loading the same ID multiple times
      if (lastLoadedIdRef.current === ruleId && !options.duplicate) {
        setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      if (isLoadingRef.current) {
        return;
      }

      setIsLoading(true);
      isLoadingRef.current = true;
      lastLoadedIdRef.current = ruleId;

      try {
        const result = await fetchAuthenticationRule(ruleId);

        const normalizedRule = normalizeRuleResponse(result);
        const normalizedCodes = normalizeCodeResponse(result.codes);

        const clonedRule = deepClone(normalizedRule);
        let clonedCodes = deepClone(normalizedCodes);

        if (options.duplicate) {
          const copySuffix = tRef.current('common:copySuffix');
          clonedRule.name = `${clonedRule.name} ${copySuffix}`.trim();
          clonedRule.context = generateContextFromName(clonedRule.name);
          clonedCodes = clonedCodes.map((code) => ({
            ...code,
            id: undefined,
            usedCount: 0,
            lastUsedAt: undefined,
            lastCaller: undefined,
          }));
          setTimeout(() => {
            setOriginalRule(deepClone(DEFAULT_RULE));
            setOriginalCodes([]);
          }, 0);
        } else {
          setTimeout(() => {
            setOriginalRule(deepClone(clonedRule));
            setOriginalCodes(deepClone(clonedCodes));
          }, 0);
        }

        setTimeout(() => {
          setFormData(clonedRule);
          setCodes(clonedCodes);
        }, 0);
      } catch (error) {
        lastLoadedIdRef.current = null;
        showErrorRef.current(tRef.current('authenticationCodes.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/auth-codes');
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          isLoadingRef.current = false;
        }, 0);
      }
    },
    [],
  );

  // Main effect to load data
  useEffect(() => {
    const idChanged = currentIdRef.current !== id;
    currentIdRef.current = id;

    if (idChanged || !hasInitializedRef.current) {
      if (isEditMode && id) {
        hasInitializedRef.current = true;
        loadRule(id, { duplicate: false });
      } else if (!isEditMode && duplicateId) {
        hasInitializedRef.current = true;
        loadRule(duplicateId, { duplicate: true });
      } else {
        hasInitializedRef.current = true;
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, [id, duplicateId, isEditMode, loadRule]);

  useEffect(() => {
    if (!isEditMode && !duplicateId && formData.name.trim()) {
      setFormData((prev) => ({ ...prev, context: generateContextFromName(prev.name) }));
    }
  }, [duplicateId, formData.name, isEditMode]);

  const hasRuleChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalRule),
    [formData, originalRule],
  );

  const hasCodeChanges = useMemo(
    () => JSON.stringify(codes) !== JSON.stringify(originalCodes),
    [codes, originalCodes],
  );

  const hasChanges = hasRuleChanges || hasCodeChanges;

  const handleFieldChange = <K extends keyof AuthenticationRuleFormState>(field: K, value: AuthenticationRuleFormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleValidateCodes = () => {
    const trimmedCodes = codes.map((code) => code.code.trim());
    if (trimmedCodes.includes('')) {
      showErrorRef.current(tRef.current('authenticationCodes.validation.codeRequired'));
      setActiveTab('codes');
      return false;
    }
    const unique = new Set(trimmedCodes);
    if (unique.size !== trimmedCodes.length) {
      showErrorRef.current(tRef.current('authenticationCodes.validation.duplicateCode'));
      setActiveTab('codes');
      return false;
    }
    return true;
  };

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = t('authenticationCodes.validation.nameRequired');
    }
    if (!formData.destinationMatchModule.trim()) {
      nextErrors.destinationMatchModule = t('authenticationCodes.validation.destinationMatchRequired');
    }
    if (!formData.destinationMatchData.trim()) {
      nextErrors.destinationMatchData = t('authenticationCodes.validation.destinationMatchDataRequired');
    }
    if (!formData.destinationFailModule.trim()) {
      nextErrors.destinationFailModule = t('authenticationCodes.validation.destinationFailRequired');
    }
    if (!formData.destinationFailData.trim()) {
      nextErrors.destinationFailData = t('authenticationCodes.validation.destinationFailDataRequired');
    }
    if (!Number.isFinite(formData.timeoutSeconds) || formData.timeoutSeconds <= 0) {
      nextErrors.timeoutSeconds = t('authenticationCodes.validation.timeoutRequired');
    }
    if (!Number.isFinite(formData.maxAttempts) || formData.maxAttempts <= 0) {
      nextErrors.maxAttempts = t('authenticationCodes.validation.maxAttemptsRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const syncCodes = useCallback(
    async (ruleId: number | string) => {
      if (!hasCodeChanges) return;

      try {
        const ruleIdentifier = ruleId || id;
        const existingById = new Map((originalCodes || []).filter((code) => code.id).map((code) => [code.id!, code]));
        const nextById = new Map(codes.filter((code) => code.id).map((code) => [code.id!, code]));

        const deletions = Array.from(existingById.keys()).filter((codeId) => !nextById.has(codeId));
        const creations = codes.filter((code) => !code.id);
        const updates = codes.filter((code) => {
          if (!code.id) return false;
          const original = existingById.get(code.id);
          return original && JSON.stringify(code) !== JSON.stringify(original);
        });

        if (deletions.length > 0) {
          await Promise.all(
            deletions.map((codeId) =>
              deleteAuthenticationCode(ruleIdentifier, codeId),
            ),
          );
        }

        if (updates.length > 0) {
          await Promise.all(
            updates.map((code) =>
              updateAuthenticationCode(ruleIdentifier, code.id!, {
                code: code.code.trim(),
                label: code.label?.trim() || null,
                enabled: code.enabled,
                settings: code.settings ?? {},
              }),
            ),
          );
        }

        if (creations.length > 0) {
          await Promise.all(
            creations.map((code) =>
              createAuthenticationCode(ruleIdentifier, {
                code: code.code.trim(),
                label: code.label?.trim() || null,
                enabled: code.enabled,
                settings: code.settings ?? {},
              }),
            ),
          );
        }
      } catch (error) {
        showErrorRef.current(tRef.current('authenticationCodes.messages.codesSyncError'));
        throw error;
      }
    },
    [codes, hasCodeChanges, id, originalCodes],
  );

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<AuthenticationRule | undefined> => {
      event?.preventDefault();

      if (!validateForm() || !handleValidateCodes()) {
        showErrorRef.current(tRef.current('authenticationCodes.messages.validationFailed', { defaultValue: tRef.current('common.validationError') }));
        return undefined;
      }

      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      setIsSaving(true);
      try {
        const promptRecordingId =
          formData.promptRecordingId && formData.promptRecordingId > 0 ? formData.promptRecordingId : null;

        const payload = {
          name: formData.name.trim(),
          context: formData.context.trim() || generateContextFromName(formData.name),
          promptRecordingId,
          timeoutSeconds: Number(formData.timeoutSeconds),
          maxAttempts: Number(formData.maxAttempts),
          destinationMatchModule: formData.destinationMatchModule.trim(),
          destinationMatchData: formData.destinationMatchData.trim(),
          destinationFailModule: formData.destinationFailModule.trim(),
          destinationFailData: formData.destinationFailData.trim(),
          enabled: formData.enabled,
          settings: formData.settings ?? {},
        };

        const result = isEditMode
          ? await updateAuthenticationRule(id!, payload)
          : await createAuthenticationRule(payload);

        const ruleId = result?.id ?? id;
        if (!ruleId) {
          throw new Error('Missing rule identifier');
        }

        if (codes.length > 0) {
          await syncCodes(ruleId);
        } else if (isEditMode && originalCodes.length > 0) {
          // If all codes were removed
          await syncCodes(ruleId);
        }

        showSuccessRef.current(
          isEditMode
            ? tRef.current('authenticationCodes.messages.updateSuccess')
            : tRef.current('authenticationCodes.messages.createSuccess'),
        );

        if (!isEditMode) {
          lastLoadedIdRef.current = null;
          hasInitializedRef.current = false;
          currentIdRef.current = undefined;
          await loadRule(String(ruleId), { duplicate: false });
        } else {
          const refreshedRule = normalizeRuleResponse(result);
          setTimeout(() => {
            setOriginalRule(deepClone(refreshedRule));
            setFormData(deepClone(refreshedRule));
          }, 0);
          if (result?.codes) {
            const refreshedCodes = normalizeCodeResponse(result.codes);
            setTimeout(() => {
              setCodes(deepClone(refreshedCodes));
              setOriginalCodes(deepClone(refreshedCodes));
            }, 0);
          } else {
            await loadRule(String(ruleId), { duplicate: false });
          }
        }

        // Return saved data for useSaveWithAction
        return result;
      } catch (error) {
        showErrorRef.current(error.message || tRef.current('authenticationCodes.messages.createError'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [
      codes,
      hasChanges,
      id,
      isEditMode,
      loadRule,
      originalCodes,
      syncCodes,
      validateForm,
      handleValidateCodes,
      formData,
    ],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as AuthenticationRule;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/auth-codes',
    newPath: '/pbx/incoming-tools/auth-codes/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Authentication Rules use ID for navigation
      return `/pbx/incoming-tools/auth-codes/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData,
      codes
    }),
    storageKey: 'authenticationCodes:saveAndNewData'
  });

  const handleReset = () => {
    if (isEditMode) {
      setFormData(deepClone(originalRule));
      setCodes(deepClone(originalCodes));
      setErrors({});
    } else {
      setFormData(deepClone(DEFAULT_RULE));
      setCodes([]);
      setErrors({});
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      handleReset();
    } else {
      navigateRef.current('/pbx/incoming-tools/auth-codes');
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteAuthenticationRule(id);
      showSuccessRef.current(tRef.current('authenticationCodes.messages.deleteSuccess'));
      navigateRef.current('/pbx/incoming-tools/auth-codes');
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('authenticationCodes.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, isEditMode]);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (id && id !== 'new') {
      navigateToAuthenticationRuleDuplicate(navigateRef.current, id);
    }
  }, [id]);

  // Load quick list
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const result = await fetchAuthenticationRules({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; name: string; description?: string | null }>)
        : [];
      setQuickListItems(items);
    } catch {
      showErrorRef.current(tRef.current('authenticationCodes.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Filter quick list
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  const handleAddCode = () => {
    setCodes((prev) => [
      ...prev,
      {
        code: '',
        label: '',
        enabled: true,
        settings: {},
      },
    ]);
  };

  const handleDeleteCode = (index: number) => {
    setShowDeleteCodeModal({ isOpen: true, codeIndex: index });
  };

  const confirmDeleteCode = () => {
    if (showDeleteCodeModal.codeIndex !== null) {
      setCodes((prev) => prev.filter((_, idx) => idx !== showDeleteCodeModal.codeIndex!));
      setShowDeleteCodeModal({ isOpen: false, codeIndex: null });
    }
  };

  const handleCodeChange = (index: number, field: keyof AuthenticationCode, value: unknown) => {
    setCodes((prev) =>
      prev.map((code, idx) =>
        idx === index
          ? {
            ...code,
            [field]: field === 'code' || field === 'label' ? String(value) : value,
          }
          : code,
      ),
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Key className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText="List"
        onBack={() => navigateRef.current('/pbx/incoming-tools/auth-codes')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('common.search', 'Search'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode && !duplicateId
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('authenticationCodes.actions.add'),
            onClick: () => navigateRef.current('/pbx/tools/authentication-codes/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'rule' | 'codes')}>
          <TabsList>
            <TabsTrigger value="rule" icon={<Key className="h-4 w-4" />}>
              {t('authenticationCodes.tabs.rule')}
            </TabsTrigger>
            <TabsTrigger value="codes" icon={<Settings className="h-4 w-4" />}>
              {t('authenticationCodes.tabs.codes')} ({codes.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rule">
            <div className="space-y-6">
              <FormBox title={t('authenticationCodes.tabs.general')} icon={<Key className="h-5 w-5" />}>
                <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Label htmlFor="name">
                    <SimpleTooltip content={t('authenticationCodes.fields.name.tooltip')}>
                      <span>
                        {t('authenticationCodes.fields.name.label')}
                        <span className="ml-1 text-red-500">*</span>
                      </span>
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(event) => {
                        const nextName = event.target.value;
                        handleFieldChange('name', nextName);
                        handleFieldChange('context', generateContextFromName(nextName));
                      }}
                      placeholder={t('authenticationCodes.fields.name.placeholder')}
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                  </div>

                  <Label htmlFor="promptRecordingId">
                    <SimpleTooltip content={t('authenticationCodes.fields.promptRecording.tooltip')}>
                      {t('authenticationCodes.fields.promptRecording.label')}
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <RecordingSelect
                      value={formData.promptRecordingId ? String(formData.promptRecordingId) : ''}
                      onChange={(value) => {
                        if (!value) {
                          handleFieldChange('promptRecordingId', null);
                          return;
                        }
                        const parsed = Number.parseInt(value, 10);
                        handleFieldChange('promptRecordingId', Number.isFinite(parsed) ? parsed : null);
                      }}
                      placeholder={t('authenticationCodes.fields.promptRecording.placeholder')}
                      returnType="id"
                      showPlayButton
                    />
                  </div>

                  <Label htmlFor="timeoutSeconds">
                    <SimpleTooltip content={t('authenticationCodes.fields.timeoutSeconds.tooltip')}>
                      <span>
                        {t('authenticationCodes.fields.timeoutSeconds.label')}
                        <span className="ml-1 text-red-500">*</span>
                      </span>
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <Input
                      id="timeoutSeconds"
                      type="number"
                      min={1}
                      value={formData.timeoutSeconds}
                      onChange={(event) => handleFieldChange('timeoutSeconds', Number(event.target.value) || 1)}
                      className={errors.timeoutSeconds ? 'border-red-500' : ''}
                    />
                    {errors.timeoutSeconds && <p className="mt-1 text-sm text-red-500">{errors.timeoutSeconds}</p>}
                  </div>

                  <Label htmlFor="maxAttempts">
                    <SimpleTooltip content={t('authenticationCodes.fields.maxAttempts.tooltip')}>
                      <span>
                        {t('authenticationCodes.fields.maxAttempts.label')}
                        <span className="ml-1 text-red-500">*</span>
                      </span>
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <Input
                      id="maxAttempts"
                      type="number"
                      min={1}
                      value={formData.maxAttempts}
                      onChange={(event) => handleFieldChange('maxAttempts', Number(event.target.value) || 1)}
                      className={errors.maxAttempts ? 'border-red-500' : ''}
                    />
                    {errors.maxAttempts && <p className="mt-1 text-sm text-red-500">{errors.maxAttempts}</p>}
                  </div>

                  <Label htmlFor="enabled">
                    <SimpleTooltip content={t('authenticationCodes.fields.enabled.tooltip')}>
                      {t('authenticationCodes.fields.enabled.label')}
                    </SimpleTooltip>
                  </Label>
                  <div>
                    <Toggle value={formData.enabled} onChange={(value) => handleFieldChange('enabled', value)} />
                  </div>
                </div>
              </FormBox>

              <FormBox title={t('authenticationCodes.fields.destinations.label')} icon={<Settings className="h-5 w-5" />}>
                <div className="space-y-6">
                  <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Label>
                      <SimpleTooltip content={t('authenticationCodes.fields.destinationMatch.tooltip')}>
                        <span>
                          {t('authenticationCodes.fields.destinationMatch.label')}
                          <span className="ml-1 text-red-500">*</span>
                        </span>
                      </SimpleTooltip>
                    </Label>
                    <div className="col-span-3">
                      <ModuleDestinationSelect
                        moduleValue={formData.destinationMatchModule}
                        destinationValue={formData.destinationMatchData}
                        onModuleChange={(value) => handleFieldChange('destinationMatchModule', value || '')}
                        onDestinationChange={(value) => handleFieldChange('destinationMatchData', value || '')}
                        modulePlaceholder={t('authenticationCodes.fields.destinationMatch.modulePlaceholder')}
                        destinationPlaceholder={t('authenticationCodes.fields.destinationMatch.destinationPlaceholder')}
                      />
                      {(errors.destinationMatchModule || errors.destinationMatchData) && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.destinationMatchModule || errors.destinationMatchData}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Label>
                      <SimpleTooltip content={t('authenticationCodes.fields.destinationFail.tooltip')}>
                        <span>
                          {t('authenticationCodes.fields.destinationFail.label')}
                          <span className="ml-1 text-red-500">*</span>
                        </span>
                      </SimpleTooltip>
                    </Label>
                    <div className="col-span-3">
                      <ModuleDestinationSelect
                        moduleValue={formData.destinationFailModule}
                        destinationValue={formData.destinationFailData}
                        onModuleChange={(value) => handleFieldChange('destinationFailModule', value || '')}
                        onDestinationChange={(value) => handleFieldChange('destinationFailData', value || '')}
                        modulePlaceholder={t('authenticationCodes.fields.destinationFail.modulePlaceholder')}
                        destinationPlaceholder={t('authenticationCodes.fields.destinationFail.destinationPlaceholder')}
                      />
                      {(errors.destinationFailModule || errors.destinationFailData) && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.destinationFailModule || errors.destinationFailData}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </FormBox>
            </div>
          </TabsContent>

          <TabsContent value="codes">
            <AuthCodesTab
              codes={codes}
              isEditMode={isEditMode}
              onAddCode={handleAddCode}
              onDeleteCode={handleDeleteCode}
              onCodeChange={handleCodeChange}
              onImport={() => setShowImportModal(true)}
              t={t}
            />
          </TabsContent>
        </Tabs>

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isEditMode={isEditMode}
          isSaving={isSaving}
          isLoading={isLoading}
          saveText={t('common:save')}
          cancelText={t('common:cancel')}
          deleteText={t('common:delete')}
          useSaveDropdown={true}
          saveDropdownStorageKey="authenticationCodes:saveActionPreference"
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('authenticationCodes.deleteEntry')}
        message={t('authenticationCodes.messages.deleteConfirmation', { name: formData.name || '' })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <ConfirmationModal
        isOpen={showDeleteCodeModal.isOpen}
        onClose={() => setShowDeleteCodeModal({ isOpen: false, codeIndex: null })}
        onConfirm={confirmDeleteCode}
        title={t('authenticationCodes.actions.deleteCode')}
        message={t('authenticationCodes.messages.deleteCodeConfirmation')}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
      />

      {isEditMode && id && id !== 'new' && (
        <ImportCodesModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={async () => {
            setShowImportModal(false);
            await loadRule(id, { duplicate: false });
            showSuccessRef.current(tRef.current('authenticationCodes.messages.importSuccess'));
          }}
          ruleId={id}
        />
      )}

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('authenticationCodes.quickList.title', 'Authentication Rules')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigateRef.current(`/pbx/incoming-tools/auth-codes/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigateRef.current(`/pbx/incoming-tools/auth-codes/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('authenticationCodes.quickList.searchPlaceholder', 'Search by name or description')}
        emptyMessage={t('authenticationCodes.quickList.empty', 'No Authentication Rules found')}
        headerIcon={<Key className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

